import { useEffect, useState, useMemo, useCallback } from 'react'
import { Popup } from 'semantic-ui-react'
import { DataCategoriesService } from '../../../services/dataCategories'
import { DataSourcesService } from '../../../services/dataSources'
import { SourceTypesService } from '../../../services/sourceTypes'
import { getApiIngestionMethod } from './ConfigureDataSources'
import {
  FieldMapping,
  fileAnalysisService,
} from '../../../services/fileAnalysisService'
import {
  FileUploadProgress,
  uploadFileToS3,
  validateFile,
} from '../../../services/fileUpload'
import type { DataCategory, SourceType } from '../../../types'
import { UpdateFrequency, ConnectionStatus, IngestionMethod } from '../../../types'
import { useAuthStore } from '../../../store/authStore'
import { extractErrorMessage } from '../../../utils/errorHandler'
import { api } from '../../../services/api'
import './AddCustomDataSource.css'
import 'semantic-ui-css/semantic.min.css'

// Data category display mapping
const DATA_CATEGORY_DISPLAY_MAP: Record<string, string> = {
  'Accounts & Relationship': 'Customer & Accounts data',
  'Portfolio & Market Data': 'Customer Portfolio data',
}

const getDisplayDataCategory = (apiValue: string): string => {
  return DATA_CATEGORY_DISPLAY_MAP[apiValue] || apiValue
}

const resolveDataCategoryName = (
  categoryValue: string,
  categories: DataCategory[]
): string => {
  const trimmed = categoryValue.trim()
  if (!trimmed) {
    return trimmed
  }
  const matched = categories.find(
    cat =>
      cat.name === trimmed || getDisplayDataCategory(cat.name) === trimmed
  )
  return matched?.name ?? trimmed
}

interface WeeklyScheduleDay {
  enabled: boolean
  time?: string
}

interface WeeklySchedule {
  monday: WeeklyScheduleDay
  tuesday: WeeklyScheduleDay
  wednesday: WeeklyScheduleDay
  thursday: WeeklyScheduleDay
  friday: WeeklyScheduleDay
  saturday: WeeklyScheduleDay
  sunday: WeeklyScheduleDay
}

interface ArrivalSchedule {
  schedule_type: 'daily' | 'weekly'
  daily_time?: string
  weekly_schedule?: WeeklySchedule
  timezone: string
}

interface S3CsvFile {
  id: string
  filename: string
  file_size: number
  upload_date: string
  data_source_name: string
  s3_url: string
}

interface FormData {
  sourceName: string
  sourceType: string
  sourceTypeId: number | null
  arrivalSchedule: ArrivalSchedule
  description: string
  primaryDataCategory: string
  defaultIngestionMethod: string
  uploadedFile: File | null
  selectedS3File: S3CsvFile | null
}

interface UploadResult {
  upload_id: number
  data_source_id: number
}

interface DataSourceCard {
  id: string
  name: string
  shortName: string
  description: string
  category: string
  categoryColor: string
  status: 'connected' | 'available'
  statusColor: string
  endpoint?: string
  ingestionMethod?: string
  lastSync?: string
  totalRecords?: number
  apiId?: number
  sourceId?: number
  // Additional API fields
  source_name?: string
  data_category_id?: number
  update_frequency?: string | null
  source_location?: string | null
  context?: string | null
  connection_status?: string | null
  priority?: number | null
  last_sync_utc?: string | null
  created_at?: string
  updated_at?: string
  created_by?: string
  updated_by?: string
  deleted_at?: string | null
  deleted_by?: string | null
  category_name?: string
  category_code?: string
  category_type?: string
  arrival_schedule?: ArrivalSchedule | null
  first_upload_id?: number | null
}

interface AddCustomDataSourceProps {
  onBackToDataSources?: () => void
  selectedDataSource?: DataSourceCard
}

const AddCustomDataSource = ({
  onBackToDataSources,
  selectedDataSource,
}: AddCustomDataSourceProps) => {
  const { isReadOnlyMode } = useAuthStore()
  const [dataCategories, setDataCategories] = useState<DataCategory[]>([])
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([])
  const [formData, setFormData] = useState<FormData>({
    sourceName: '',
    sourceType: '',
    sourceTypeId: null,
    arrivalSchedule: {
      schedule_type: 'weekly',
      weekly_schedule: {
        monday: { enabled: true, time: '09:00:00' },
        tuesday: { enabled: false },
        wednesday: { enabled: false },
        thursday: { enabled: false },
        friday: { enabled: false },
        saturday: { enabled: false },
        sunday: { enabled: false },
      },
      timezone: 'UTC',
    },
    description: '',
    primaryDataCategory: '',
    defaultIngestionMethod: IngestionMethod.FILE_UPLOAD,
    uploadedFile: null,
    selectedS3File: null,
  })
  const [dragActive, setDragActive] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isUploaded, setIsUploaded] = useState(false)
  const [uploadProgress, setUploadProgress] =
    useState<FileUploadProgress | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [showFieldDefinitions, setShowFieldDefinitions] = useState(false)
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean
  }>({
    fieldDefinitions: true,
    validation: true,
    conflict: true,
  })
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10
  const [ruleDescription, setRuleDescription] = useState('')
  const [selectedValidationField, setSelectedValidationField] =
    useState('client_identifier')
  const [validationRules, setValidationRules] = useState<
    Array<{
      id: string
      fieldName: string
      description: string
      createdAt: string
    }>
  >([])
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [criticalFieldError, setCriticalFieldError] = useState<string | null>(null)
  const [missingCriticalFields, setMissingCriticalFields] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [canonicalFields, setCanonicalFields] = useState<string[]>([])
  const [isSavingRule, setIsSavingRule] = useState(false)
  const [ruleSaveError, setRuleSaveError] = useState<string | null>(null)
  const [selectedConflictField, setSelectedConflictField] = useState('')
  const [selectedResolutionStrategy, setSelectedResolutionStrategy] =
    useState('Use Latest Value')
  const [resolutionStrategies, setResolutionStrategies] = useState<
    Array<{
      id: string
      fieldName: string
      strategy: string
      description: string
      createdAt: string
    }>
  >([])
  const [isSavingStrategy, setIsSavingStrategy] = useState(false)
  const [strategySaveError, setStrategySaveError] = useState<string | null>(
    null
  )
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingError, setProcessingError] = useState<string | null>(null)
  const [processingSuccess, setProcessingSuccess] = useState(false)
  const [deletingRuleId, setDeletingRuleId] = useState<string | null>(null)
  const [deletingStrategyId, setDeletingStrategyId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'dataSource' | 'canonicalFields' | 'validation' | 'conflictResolution'>('dataSource')

  // S3 CSV file dropdown state
  const [s3CsvFiles, setS3CsvFiles] = useState<S3CsvFile[]>([])
  const [isLoadingS3Files, setIsLoadingS3Files] = useState(false)
  const [s3FilesError, setS3FilesError] = useState<string | null>(null)
  const [isSelectingS3File, setIsSelectingS3File] = useState(false)

  // CSV preview modal state
  const [showCsvPreview, setShowCsvPreview] = useState(false)
  const [csvPreviewHeaders, setCsvPreviewHeaders] = useState<string[]>([])
  const [csvPreviewRows, setCsvPreviewRows] = useState<string[][]>([])
  const [csvPreviewLoading, setCsvPreviewLoading] = useState(false)
  const [csvPreviewError, setCsvPreviewError] = useState<string | null>(null)

  // Individual field validation state
  const [fieldErrors, setFieldErrors] = useState<{
    sourceName?: string
    sourceType?: string
    description?: string
    primaryDataCategory?: string
    defaultIngestionMethod?: string
    uploadedFile?: string
  }>({})
  const [showFieldErrors, setShowFieldErrors] = useState(false)

  // Fetch S3 CSV files from API
  const fetchS3CsvFiles = async () => {
    try {
      setIsLoadingS3Files(true)
      setS3FilesError(null)

      // Use the configured API service - it handles authentication automatically
      const response = await api.get('/s3-csv-files/')

      if (response.data.success && response.data.data?.files && Array.isArray(response.data.data.files)) {
        // Map the API response to match our S3CsvFile interface
        const mappedFiles: S3CsvFile[] = response.data.data.files.map((file: any, index: number) => ({
          id: file.key || `file_${index}`, // Use key as ID, fallback to index
          filename: file.filename,
          file_size: file.size,
          upload_date: file.last_modified,
          data_source_name: file.filename.replace('.csv', ''), // Extract name from filename
          s3_url: file.url
        }))
        setS3CsvFiles(mappedFiles)
      } else {
        throw new Error('Invalid response format from S3 CSV files API')
      }

    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to load S3 CSV files')
      setS3FilesError(errorMessage)
      setS3CsvFiles([])
    } finally {
      setIsLoadingS3Files(false)
    }
  }

  // Handle S3 file dropdown change — just store the selection, no API call yet
  const handleS3FileChange = (s3File: S3CsvFile) => {
    setFormData(prev => ({
      ...prev,
      selectedS3File: s3File,
      uploadedFile: null,
    }))
    setIsUploaded(false)
    setUploadResult(null)
    setSubmitError(null)
  }

  // Handle CSV preview — fetches and parses first 50 rows from S3
  const handleCsvPreview = async (s3File: S3CsvFile) => {
    setShowCsvPreview(true)
    setCsvPreviewLoading(true)
    setCsvPreviewError(null)
    setCsvPreviewHeaders([])
    setCsvPreviewRows([])

    const parseRow = (line: string): string[] => {
      const result: string[] = []
      let current = ''
      let inQuotes = false
      for (let i = 0; i < line.length; i++) {
        const ch = line[i]
        if (ch === '"') {
          inQuotes = !inQuotes
        } else if (ch === ',' && !inQuotes) {
          result.push(current)
          current = ''
        } else {
          current += ch
        }
      }
      result.push(current)
      return result
    }

    const parseAndSet = (text: string): boolean => {
      const lines = text.split('\n').filter(l => l.trim().length > 0)
      if (lines.length === 0) return false
      setCsvPreviewHeaders(parseRow(lines[0]))
      setCsvPreviewRows(lines.slice(1, 51).map(parseRow))
      return true
    }

    // Try the real backend endpoint first (works in both dev and production),
    // then fall back to the local Vite dev proxy for offline/local-only dev.
    const backendEndpoints = [
      () => api.get('/s3-csv-files/content', { params: { key: s3File.id }, responseType: 'text' }),
      () => fetch(`/s3-proxy?url=${encodeURIComponent(s3File.s3_url)}`).then(async r => {
        if (!r.ok) throw new Error(`${r.status}`)
        const text = await r.text()
        const trimmed = text.trimStart()
        if (trimmed.startsWith('<!DOCTYPE') || trimmed.startsWith('<html')) {
          throw new Error('proxy-not-available')
        }
        return { data: text }
      }),
    ]

    for (const callEndpoint of backendEndpoints) {
      try {
        const res = await callEndpoint()
        const text = typeof res.data === 'string' ? res.data : JSON.stringify(res.data)
        if (parseAndSet(text)) {
          setCsvPreviewLoading(false)
          return
        }
      } catch {
        // try next
      }
    }

    // Last resort: direct presigned URL (works only if S3 CORS allows it)
    try {
      const res = await fetch(s3File.s3_url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const text = await res.text()
      if (!parseAndSet(text)) {
        setCsvPreviewError('The file appears to be empty.')
      }
    } catch {
      setCsvPreviewError(
        'Unable to load preview. The S3 bucket blocks direct browser access (CORS). Please ensure the backend is running and try again, or use the Download button to open the file.'
      )
    } finally {
      setCsvPreviewLoading(false)
    }
  }

  // Handle S3 file confirm (button click) — creates data source + links file
  const handleS3FileSelect = async (s3File: S3CsvFile) => {
    if (!s3File) {
      setSubmitError('Please select a valid S3 file')
      return
    }

    // Validate all required fields before selecting S3 file
    const isValid = validateFields(true) // Skip file validation
    if (!isValid) {
      setSubmitError('Please fill in all required fields before selecting the S3 file')
      setShowFieldErrors(true)
      return
    }

    setIsSelectingS3File(true)
    setSubmitError(null)

    try {
      // Update form data with selected S3 file
      setFormData(prev => ({
        ...prev,
        selectedS3File: s3File,
        uploadedFile: null // Clear manual upload if any
      }))

      let dataSourceId: number

      // If editing an existing data source, update it first
      if (selectedDataSource) {
        const matchingSourceType = sourceTypes.find(
          st => st.display_name === formData.sourceType
        )

        const matchingCategory = dataCategories.find(
          cat => cat.name === formData.primaryDataCategory
        )

        const updateData = {
          source_name: formData.sourceName,
          source_type_id: matchingSourceType?.id || (selectedDataSource.sourceId || 1),
          data_category_id: Number(matchingCategory?.id || selectedDataSource.data_category_id || 1),
          ingestion_method: getApiIngestionMethod(formData.defaultIngestionMethod),
          context: formData.description,
          connection_status: selectedDataSource.connection_status || 'connected',
          priority: selectedDataSource.priority || 1,
        }

        const dataSourceIdString = String(selectedDataSource.apiId || selectedDataSource.sourceId || selectedDataSource.id)
        await DataSourcesService.updateDataSource(dataSourceIdString, updateData)

        dataSourceId = selectedDataSource.apiId || selectedDataSource.sourceId || parseInt(selectedDataSource.id, 10)
      } else {
        // Create new data source (same as manual upload workflow)
        console.log('Creating new data source for S3 CSV:', formData)

        // Find category ID for selected dropdown label or id
        const selectedCategory = dataCategories.find(cat =>
          typeof cat === 'string'
            ? cat === formData.primaryDataCategory
            : cat.name === formData.primaryDataCategory ||
              cat.id === formData.primaryDataCategory
        )
        const data_category_id =
          typeof selectedCategory === 'string' ? undefined : selectedCategory?.id

        // Send all entered fields to backend
        const sourceData = {
          source_name: formData.sourceName,
          source_type_id: formData.sourceTypeId,
          data_category_id,
          ingestion_method: getApiIngestionMethod(formData.defaultIngestionMethod),
          update_frequency: UpdateFrequency.DAILY,
          context: formData.description,
          connection_status: ConnectionStatus.CONNECTED,
          priority: 1,
          arrival_schedule: formData.arrivalSchedule,
        }

        const dataSourceResponse = await DataSourcesService.createDataSource(sourceData as any)

        if (!dataSourceResponse.success || !dataSourceResponse.data) {
          throw new Error('Failed to create data source')
        }

        dataSourceId = dataSourceResponse.data.id
        console.log('Data source created successfully with ID:', dataSourceId)
      }

      // Ensure dataSourceId is a valid integer before making API call
      if (!dataSourceId || isNaN(dataSourceId)) {
        throw new Error('Invalid data source ID. Cannot proceed with S3 file selection.')
      }

      // Call the S3 file select API with correct field names matching backend expectations
      const selectRequest = {
        s3_key: s3File.id, // Use s3_key instead of s3_file_key
        data_source_id: Number(dataSourceId), // Ensure it's a valid integer
        filename: s3File.filename // Include required filename field
      }

      console.log('Selecting S3 file with request:', selectRequest)
      const response = await api.post('/s3-csv-files/select', selectRequest)

      if (response.data.success && response.data.data) {
        // Extract upload result from response
        const responseData = response.data.data

        setIsUploaded(true)
        setUploadResult({
          upload_id: responseData.upload_id || responseData.id || 0,
          data_source_id: dataSourceId,
        })

        // Clear any file-related errors
        if (fieldErrors.uploadedFile) {
          setFieldErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.uploadedFile
            return newErrors
          })
        }

        const message = selectedDataSource
          ? '✓ Data source updated and S3 file selected successfully!'
          : '✓ Data source created and S3 file selected successfully!'
        setSubmitError(message)
        setTimeout(() => setSubmitError(null), 3000)
      } else {
        throw new Error('Failed to select S3 file')
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'S3 file selection failed')
      setSubmitError(errorMessage)
    } finally {
      setIsSelectingS3File(false)
    }
  }

  // Form validation - check if all required fields are filled
  const isFormValid = useMemo(() => {
    // All required fields must be filled
    const hasSourceName = formData.sourceName.trim().length > 0
    const hasSourceType = formData.sourceTypeId !== null
    const hasDescription = formData.description.trim().length > 0
    const hasPrimaryDataCategory = formData.primaryDataCategory.trim().length > 0
    const hasDefaultIngestionMethod = formData.defaultIngestionMethod.trim().length > 0
    const hasUploadedFile = formData.uploadedFile !== null
    const hasSelectedS3File = formData.selectedS3File !== null
    const fileUploaded = isUploaded

    // Existing source with a linked upload (FILE UPLOADED) — don't require re-picking S3
    if (
      selectedDataSource &&
      (uploadResult || selectedDataSource.first_upload_id)
    ) {
      return (
        hasSourceName &&
        hasSourceType &&
        hasDescription &&
        hasPrimaryDataCategory &&
        hasDefaultIngestionMethod
      )
    }

    // Check ingestion method to determine file requirement
    const isS3CsvMethod = formData.defaultIngestionMethod === IngestionMethod.S3_CSV

    // For new data sources or editing, file/S3 selection is required based on ingestion method
    if (isS3CsvMethod) {
      // S3 CSV method requires S3 file selection and upload completion
      return (
        hasSourceName &&
        hasSourceType &&
        hasDescription &&
        hasPrimaryDataCategory &&
        hasDefaultIngestionMethod &&
        hasSelectedS3File &&
        fileUploaded
      )
    } else {
      // File upload method requires manual file upload
      return (
        hasSourceName &&
        hasSourceType &&
        hasDescription &&
        hasPrimaryDataCategory &&
        hasDefaultIngestionMethod &&
        hasUploadedFile &&
        fileUploaded
      )
    }
  }, [formData, isUploaded, selectedDataSource, uploadResult])

  // Validate individual fields and update error messages
  const validateFields = useCallback((skipFileValidation = false) => {
    const errors: typeof fieldErrors = {}

    // Check each required field
    if (!formData.sourceName.trim()) {
      errors.sourceName = 'Source name is required'
    }

    if (!formData.sourceTypeId) {
      errors.sourceType = 'Source type is required'
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required'
    }

    if (!formData.primaryDataCategory.trim()) {
      errors.primaryDataCategory = 'Primary data category is required'
    }

    if (!formData.defaultIngestionMethod.trim()) {
      errors.defaultIngestionMethod = 'Default ingestion method is required'
    }

    // Check file upload/S3 selection for new data sources (only if not skipping)
    if (!skipFileValidation) {
      const isNewDataSource = !selectedDataSource
      const isEditingWithoutUploadResult = selectedDataSource && !uploadResult
      const needsFileUpload = isNewDataSource || isEditingWithoutUploadResult

      if (needsFileUpload) {
        const isS3CsvMethod = formData.defaultIngestionMethod === IngestionMethod.S3_CSV

        if (isS3CsvMethod) {
          // S3 CSV method validation
          if (!formData.selectedS3File) {
            errors.uploadedFile = 'Please select an S3 file'
          } else if (!isUploaded) {
            errors.uploadedFile = 'Please confirm the S3 file selection'
          }
        } else {
          // Manual file upload validation
          if (!formData.uploadedFile) {
            errors.uploadedFile = 'Please select a file to upload'
          } else if (!isUploaded) {
            errors.uploadedFile = 'Please upload the selected file'
          }
        }
      }
    }

    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }, [formData, selectedDataSource, uploadResult, isUploaded])

  // Helper function to get field data type
  const getFieldDataType = (fieldName: string): string => {
    const fieldTypes: Record<string, string> = {
      client_identifier: 'String',
      account_reference: 'String',
      client_full_name: 'String',
      first_name: 'String',
      last_name: 'String',
      birth_date: 'Date',
      date_of_birth: 'Date',
      social_security_number: 'String',
      contact_phone: 'String',
      primary_phone_number: 'String',
      email_address: 'String',
      employment_position_title: 'String',
      employer_company_name: 'String',
      employment_start_date: 'Date',
      is_active_flag: 'Boolean',
      investment_risk_tolerance: 'String',
      address_line_1: 'String',
      address_line_2: 'String',
      road_name: 'String',
      city: 'String',
      state_code: 'String',
      country_name: 'String',
      postal_code: 'String',
      emergency_contact_details: 'String',
    }
    return fieldTypes[fieldName] || 'String'
  }

  const loadValidationRules = async () => {
    if (!uploadResult) return

    try {
      
      const response = await fileAnalysisService.getValidationRules(
        uploadResult.upload_id,
        uploadResult.data_source_id
      )

      if (response.success && response.data.rules_count > 0) {
        const rules = response.data.validation_rules.map((rule, index) => ({
          id: `rule_${index}`,
          fieldName: rule.canonical_field,
          description: rule.validation_instruction,
          createdAt: new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }),
        }))
        setValidationRules(rules)
      }
    } catch (error) {
      console.error('Error loading validation rules:', error)
    }
  }

  type SchemaAnalysisOutcome =
    | 'success'
    | 'empty'
    | 'error'
    | 'critical'
    | 'no_category'

  const goToCanonicalFieldsTab = () => {
    setIsCollapsed(true)
    setShowFieldDefinitions(true)
    setActiveTab('canonicalFields')
  }

  const runSchemaMappingAnalysis = useCallback(
    async (
      uploadId: number,
      dataSourceId: number,
      categoryOverride?: string
    ): Promise<SchemaAnalysisOutcome> => {
      // Prefer explicit category (avoids stale formData when opening an existing source)
      const category = resolveDataCategoryName(
        categoryOverride || formData.primaryDataCategory,
        dataCategories
      )
      if (!category) {
        setAnalysisError('Data category is required before schema analysis can run')
        return 'no_category'
      }

      setIsAnalyzing(true)
      setAnalysisError(null)
      setCriticalFieldError(null)
      setMissingCriticalFields([])

      try {
        console.log(
          'Running schema mapping analysis',
          { uploadId, dataSourceId, category }
        )
        const analysisResponse = await fileAnalysisService.analyzeUploadedFile({
          upload_id: uploadId,
          data_source_id: dataSourceId,
          category,
        })

        if (analysisResponse.critical_field_error) {
          setCriticalFieldError(analysisResponse.critical_field_error)
          if (analysisResponse.missing_critical_fields) {
            setMissingCriticalFields(analysisResponse.missing_critical_fields)
          }
          return 'critical'
        }

        const mappings =
          fileAnalysisService.convertToFieldMappings(analysisResponse)
        setFieldMappings(mappings)
        setCurrentPage(1)

        const fields = mappings.map(m => m.fieldName)
        setCanonicalFields(fields)

        if (analysisResponse.stored_rules) {
          const newRules = Object.entries(analysisResponse.stored_rules).map(
            ([fieldName, description], index) => ({
              id: `stored_${index}`,
              fieldName,
              description,
              createdAt: new Date().toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
            })
          )
          setValidationRules(prev => [...prev, ...newRules])
        }

        setUploadResult({ upload_id: uploadId, data_source_id: dataSourceId })
        setIsUploaded(true)

        if (mappings.length === 0) {
          setAnalysisError(
            'Schema Mapping agent returned no field mappings for this file. Check the CSV headers/category, or retry after confirming the Schema Mapping agent is healthy in UAT.'
          )
          return 'empty'
        }
        return 'success'
      } catch (error) {
        console.error('Schema mapping analysis failed:', error)
        const errorMessage = extractErrorMessage(
          error,
          'Failed to analyze uploaded file'
        )
        setAnalysisError(errorMessage)
        return 'error'
      } finally {
        setIsAnalyzing(false)
      }
    },
    [dataCategories, formData.primaryDataCategory]
  )

  // Load existing data when viewing an available data source (without upload)
  const loadExistingDataSourceData = async (
    dataSourceId: number,
    firstUploadId?: number | null,
    categoryName?: string
  ) => {
    try {
      
      setIsAnalyzing(true)
      setAnalysisError(null)

      let hasAnyData = false
      let hasFieldMappings = false

      if (firstUploadId) {
        setUploadResult({
          upload_id: firstUploadId,
          data_source_id: dataSourceId,
        })
        setIsUploaded(true)
      }

      // Load field mappings
      try {
        
        const fieldMappingsResponse = await fileAnalysisService.getFieldMappings(dataSourceId)
       
        
        if (fieldMappingsResponse.success && fieldMappingsResponse.data.mappings_count > 0) {
          const mappings = fieldMappingsResponse.data.field_mappings.map((mapping, index) => ({
            id: index + 1,
            fieldName: mapping.canonical_field,
            originalName: mapping.original_field,
            dataType: mapping.data_type || getFieldDataType(mapping.canonical_field),
            useOriginal: false,
            mapping: mapping.original_field,
            isPreferred: (mapping.confidence_score || 0) >= 0.9,
            confidenceScore: mapping.confidence_score,
          }))
          setFieldMappings(mappings)
          
          
          // Extract canonical fields
          const fields = mappings.map(m => m.fieldName)
          setCanonicalFields(fields)
          
          hasAnyData = true
          hasFieldMappings = true
        } else {
          console.log('No field mappings found or mappings_count is 0')
        }
      } catch (error: any) {
        console.error('Error loading field mappings:', error)
        // If it's a 404, it means no data exists yet - this is ok
        if (error?.response?.status !== 404) {
          console.error('Unexpected error loading field mappings:', error)
        }
      }

      if (!hasFieldMappings && firstUploadId) {
        console.log(
          'No saved mappings; invoking Schema Mapping agent for upload',
          firstUploadId
        )
        const outcome = await runSchemaMappingAnalysis(
          firstUploadId,
          dataSourceId,
          categoryName
        )
        if (outcome === 'success') {
          hasAnyData = true
          hasFieldMappings = true
        }
      }

      // Load validation rules
      try {
        
        const uploadId = firstUploadId || 0
        const validationResponse = await fileAnalysisService.getValidationRules(uploadId, dataSourceId)
        
        
        if (validationResponse.success && validationResponse.data.rules_count > 0) {
          const rules = validationResponse.data.validation_rules.map((rule, index) => ({
            id: `rule_${index}`,
            fieldName: rule.canonical_field,
            description: rule.validation_instruction,
            createdAt: new Date().toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }),
          }))
          setValidationRules(rules)
          
          hasAnyData = true
        } else {
          console.log('No validation rules found or rules_count is 0')  
        }
      } catch (error: any) {
        console.error('Error loading validation rules:', error)
        // If it's a 404, it means no data exists yet - this is ok
        if (error?.response?.status !== 404) {
          console.error('Unexpected error loading validation rules:', error)
        }
      }

      // Load resolution strategies
      try {
        
        const uploadId = firstUploadId || 0
        const strategiesResponse = await fileAnalysisService.getResolutionStrategies(uploadId, dataSourceId)
        
        
        if (strategiesResponse.success && strategiesResponse.data.strategies_count > 0) {
          const strategies = strategiesResponse.data.resolution_strategies.map((strategy, index) => ({
            id: `strategy_${index}`,
            fieldName: strategy.canonical_field,
            strategy: reverseStrategyMapping[strategy.resolution_strategy] || strategy.resolution_strategy,
            description: getStrategyDescription(strategy.resolution_strategy),
            createdAt: new Date(strategy.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }),
          }))
          setResolutionStrategies(strategies)
          
          hasAnyData = true
        } else {
          console.log('No resolution strategies found or strategies_count is 0')
        }
      } catch (error: any) {
        console.error('Error loading resolution strategies:', error)
        // If it's a 404, it means no data exists yet - this is ok
        if (error?.response?.status !== 404) {
          console.error('Unexpected error loading resolution strategies:', error)
        }
      }

      if (!hasAnyData) {
        console.log('No pipeline configuration found for this data source yet')
      }

    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to load data source data')
      setAnalysisError(errorMessage)
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleGenerateRule = async () => {
    if (!ruleDescription.trim()) {
      setRuleSaveError('Please enter a validation rule description')
      return
    }

    if (!uploadResult) {
      setRuleSaveError('Upload result not available')
      return
    }

    setIsSavingRule(true)
    setRuleSaveError(null)

    try {
      console.log('Saving validation rule with upload_id:', uploadResult.upload_id)
      const saveRequest = {
        upload_id: uploadResult.upload_id,
        data_source_id: uploadResult.data_source_id,
        validation_rules: [
          {
            field: selectedValidationField,
            validation_instruction: ruleDescription.trim(),
          },
        ],
      }

      const response =
        await fileAnalysisService.saveValidationRules(saveRequest)

      if (response.success) {
        // Clear the input
        setRuleDescription('')

        // Reload validation rules to show the updated list
        await loadValidationRules()

        // Update the selected field to the first available field
        const availableFields = canonicalFields.filter(
          field => !validationRules.some(rule => rule.fieldName === field) && field !== selectedValidationField
        )
        if (availableFields.length > 0) {
          setSelectedValidationField(availableFields[0])
        }
      } else {
        throw new Error('Failed to save validation rule')
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save validation rule')
      setRuleSaveError(errorMessage)
    } finally {
      setIsSavingRule(false)
    }
  }

  const handleClearRule = () => {
    setRuleDescription('')
    setRuleSaveError(null)
  }

  const handleDeleteRule = async (ruleId: string) => {
    const ruleToDelete = validationRules.find(rule => rule.id === ruleId)
    if (!ruleToDelete || !uploadResult) {
      console.error('Rule or upload result not found')
      return
    }

    setDeletingRuleId(ruleId)

    try {
      // Call the DELETE API
      const response = await fileAnalysisService.deleteValidationRule(
        uploadResult.data_source_id,
        ruleToDelete.fieldName
      )

      if (response.success) {
        // Remove rule from local state
        setValidationRules(prev => prev.filter(rule => rule.id !== ruleId))

        // If no field is selected or current field is unavailable, set it to the deleted field
        const remainingRules = validationRules.filter(rule => rule.id !== ruleId)
        if (!selectedValidationField || remainingRules.some(rule => rule.fieldName === selectedValidationField)) {
          setSelectedValidationField(ruleToDelete.fieldName)
        }
      } else {
        console.error('Failed to delete validation rule:', response.message)
      }
    } catch (error) {
      console.error('Error deleting validation rule:', error)
    } finally {
      setDeletingRuleId(null)
    }
  }

  const loadResolutionStrategies = async () => {
    if (!uploadResult) return

    try {
      const response = await fileAnalysisService.getResolutionStrategies(
        uploadResult.upload_id,
        uploadResult.data_source_id
      )

      if (response.success && response.data.strategies_count > 0) {
        const strategies = response.data.resolution_strategies.map(
          (strategy, index) => ({
            id: `strategy_${index}`,
            fieldName: strategy.canonical_field,
            strategy:
              reverseStrategyMapping[strategy.resolution_strategy] ||
              strategy.resolution_strategy,
            description: getStrategyDescription(strategy.resolution_strategy),
            createdAt: new Date(strategy.created_at).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }),
          })
        )
        setResolutionStrategies(strategies)
      }
    } catch (error) {
      console.error('Error loading resolution strategies:', error)
    }
  }

  const getStrategyDescription = (strategy: string): string => {
    const descriptions: Record<string, string> = {
      LATEST_TIMESTAMP: 'Use the most recently updated value from data sources',
      AI_INTELLIGENCE:
        'Apply AI to determine the most accurate value based on data context',
      SOURCE_PRIORITY: 'Use value from highest priority data source',
    }
    return descriptions[strategy] || 'Custom resolution strategy'
  }

  const handleAddResolution = async () => {
    if (!selectedConflictField || !uploadResult) {
      setStrategySaveError(
        'Please select a field and ensure upload is available'
      )
      return
    }

    setIsSavingStrategy(true)
    setStrategySaveError(null)

    try {
      console.log('Saving resolution strategy with upload_id:', uploadResult.upload_id)
      const saveRequest = {
        upload_id: uploadResult.upload_id,
        data_source_id: uploadResult.data_source_id,
        resolution_strategies: [
          {
            field: selectedConflictField,
            strategy: strategyMapping[selectedResolutionStrategy],
          },
        ],
      }

      const response =
        await fileAnalysisService.saveResolutionStrategies(saveRequest)

      if (response.success) {
        console.log(
          `Successfully saved ${response.data.strategies_saved} resolution strategy(s)`
        )

        // Reload resolution strategies to show the updated list
        await loadResolutionStrategies()

        // Update the selected field to the first available field
        const availableFields = canonicalFields.filter(
          field => !resolutionStrategies.some(strategy => strategy.fieldName === field) && field !== selectedConflictField
        )
        if (availableFields.length > 0) {
          setSelectedConflictField(availableFields[0])
        }
      } else {
        throw new Error('Failed to save resolution strategy')
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save resolution strategy')
      setStrategySaveError(errorMessage)
    } finally {
      setIsSavingStrategy(false)
    }
  }

  const handleDeleteStrategy = async (strategyId: string) => {
    const strategyToDelete = resolutionStrategies.find(strategy => strategy.id === strategyId)
    if (!strategyToDelete || !uploadResult) {
      console.error('Strategy or upload result not found')
      return
    }

    setDeletingStrategyId(strategyId)

    try {
      console.log('Deleting resolution strategy with upload_id:', uploadResult.upload_id)
      // Call the DELETE API
      const response = await fileAnalysisService.deleteResolutionStrategy(
        uploadResult.upload_id,
        strategyToDelete.fieldName
      )

      if (response.success) {
        // Remove strategy from local state
        setResolutionStrategies(prev =>
          prev.filter(strategy => strategy.id !== strategyId)
        )

        // If no field is selected or current field is unavailable, set it to the deleted field
        const remainingStrategies = resolutionStrategies.filter(strategy => strategy.id !== strategyId)
        if (!selectedConflictField || remainingStrategies.some(strategy => strategy.fieldName === selectedConflictField)) {
          setSelectedConflictField(strategyToDelete.fieldName)
        }
      } else {
        console.error('Failed to delete resolution strategy:', response.message)
      }
    } catch (error) {
      console.error('Error deleting resolution strategy:', error)
    } finally {
      setDeletingStrategyId(null)
    }
  }

  const handleStartProcessing = async () => {
    if (!uploadResult) {
      setProcessingError('Upload result not available')
      return
    }

    setIsProcessing(true)
    setProcessingError(null)
    setProcessingSuccess(false)
    setCriticalFieldError(null)

    try {
      const categoryName = resolveDataCategoryName(
        formData.primaryDataCategory,
        dataCategories
      )

      if (!categoryName) {
        throw new Error('Data category not available')
      }

      console.log('Starting processing with upload_id:', uploadResult.upload_id)
      const processingRequest = {
        upload_id: uploadResult.upload_id,
        data_source_id: uploadResult.data_source_id,
        category: categoryName,
        processing_method: 'Manual Upload',
      }

      console.log('Triggering processing with request:', processingRequest)

      const response =
        await fileAnalysisService.triggerProcessing(processingRequest)

      if (response.success) {
        setProcessingSuccess(true)
        console.log('Processing triggered successfully:', response.data)
      } else {
        throw new Error('Failed to trigger processing')
      }
    } catch (error: any) {
      console.error('Error triggering processing:', error)

      if (
        error?.error_code === 'CONFIGURATION_INCOMPLETE' &&
        error?.details?.missing_configs?.includes('critical_fields') &&
        error?.details?.critical_field_error
      ) {
        setCriticalFieldError(error.details.critical_field_error)
        setProcessingError(
          'Cannot process file: Missing required identity fields. Please upload a different file with the required fields.'
        )
      } else {
        const errorMessage = extractErrorMessage(error, 'Failed to start processing')
        setProcessingError(errorMessage)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  const toggleSectionExpand = (sectionName: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName],
    }))
  }

  // Pagination logic
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedFields = fieldMappings.slice(
    startIndex,
    startIndex + itemsPerPage
  )

  const ingestionMethods = Object.values(IngestionMethod)

  const resolutionStrategyOptions = [
    'Use Latest Value',
    'Use AI Intelligence',
    'Use DataSource Priority',
  ]

  const strategyMapping: Record<string, string> = {
    'Use Latest Value': 'LATEST_TIMESTAMP',
    'Use AI Intelligence': 'AI_INTELLIGENCE',
    'Use DataSource Priority': 'SOURCE_PRIORITY',
  }

  const reverseStrategyMapping: Record<string, string> = {
    LATEST_TIMESTAMP: 'Use Latest Value',
    AI_INTELLIGENCE: 'Use AI Intelligence',
    SOURCE_PRIORITY: 'Use DataSource Priority',
  }

  // Fetch data categories and source types on component mount
  useEffect(() => {
    const fetchDataCategories = async () => {
      try {
        const response = await DataCategoriesService.getDataCategories()
        if (response.data && Array.isArray(response.data)) {
          setDataCategories(response.data)
          // Set "Accounts & Relationship" as default if creating a new data source and no category is set
          if (response.data.length > 0 && !formData.primaryDataCategory && !selectedDataSource) {
            // Find "Accounts & Relationship" category (displays as "Customer & Accounts data")
            const accountsCategory = response.data.find(cat => cat.name === 'Accounts & Relationship')
            const defaultCategory = accountsCategory || response.data[0]

            setFormData(prev => ({
              ...prev,
              primaryDataCategory: defaultCategory.name,
            }))
          }
        }
      } catch (error) {
        console.error('Error fetching data categories:', error)
      }
    }

    const fetchSourceTypes = async () => {
      try {
        const response = await SourceTypesService.getSourceTypes({ is_active: true })
        if (response.data && Array.isArray(response.data)) {
          // Sort by sort_order
          const sortedTypes = response.data.sort((a, b) => a.sort_order - b.sort_order)
          setSourceTypes(sortedTypes)
          // Set first source type as default if form doesn't have one yet
          if (sortedTypes.length > 0 && !formData.sourceTypeId) {
            setFormData(prev => ({
              ...prev,
              sourceType: sortedTypes[0].display_name,
              sourceTypeId: sortedTypes[0].id,
            }))
          }
        }
      } catch (error) {
        console.error('Error fetching source types:', error)
      }
    }

    fetchDataCategories()
    fetchSourceTypes()
  }, [])

  // Pre-populate form when a data source is selected from the grid
  useEffect(() => {
    if (selectedDataSource && sourceTypes.length > 0) {
      // Debug: Log the entire selectedDataSource object to see what fields are available
      console.log('selectedDataSource object:', selectedDataSource)
      console.log('selectedDataSource keys:', Object.keys(selectedDataSource))
      
      // Try to find matching source type from the fetched source types
      const matchingSourceType = sourceTypes.find(
        st => st.display_name === selectedDataSource.category_type ||
              st.display_name === selectedDataSource.category ||
              st.code === selectedDataSource.category_type
      )

      // Parse arrival_schedule from the data source
      let arrivalSchedule: ArrivalSchedule = {
        schedule_type: 'weekly',
        weekly_schedule: {
          monday: { enabled: false },
          tuesday: { enabled: false },
          wednesday: { enabled: false },
          thursday: { enabled: false },
          friday: { enabled: false },
          saturday: { enabled: false },
          sunday: { enabled: false },
        },
        timezone: 'UTC',
      }

      if (selectedDataSource.arrival_schedule) {
        arrivalSchedule = selectedDataSource.arrival_schedule
        console.log('✓ Loaded arrival schedule:', arrivalSchedule)
      } else {
        console.log('❌ No arrival_schedule found in selectedDataSource')
      }

      // Force a complete form reset with new data
      const newFormData: FormData = {
        sourceName: selectedDataSource.source_name || selectedDataSource.name || '',
        sourceType: matchingSourceType?.display_name || selectedDataSource.category_type || selectedDataSource.category || '',
        sourceTypeId: matchingSourceType?.id || null,
        arrivalSchedule: arrivalSchedule,
        description: selectedDataSource.context || selectedDataSource.description || '',
        primaryDataCategory: selectedDataSource.category_name || '',
        defaultIngestionMethod: selectedDataSource.ingestionMethod || IngestionMethod.FILE_UPLOAD,
        uploadedFile: null,
        selectedS3File: null,
      };

      // Set the complete new form data
      setFormData(newFormData);

      // Show the Field Definitions section immediately but stay on first tab
      setShowFieldDefinitions(true)
      setActiveTab('dataSource')

      // Load existing data for data sources (not requiring upload first)
      const dataSourceId = selectedDataSource.apiId || selectedDataSource.sourceId || parseInt(selectedDataSource.id, 10)
      const firstUploadId = selectedDataSource.first_upload_id
      const categoryForAnalysis = newFormData.primaryDataCategory
      
      
      if (dataSourceId) {
        console.log('Loading existing data for data source ID:', dataSourceId, 'with first_upload_id:', firstUploadId)
        loadExistingDataSourceData(dataSourceId, firstUploadId, categoryForAnalysis)
      }
    }
  }, [selectedDataSource, sourceTypes])

  // Load validation rules when uploadResult is available
  useEffect(() => {
    if (uploadResult && canonicalFields.length > 0) {
      loadValidationRules()
      loadResolutionStrategies()
      // Set first available canonical field as default for conflict resolution
      const availableConflictFields = canonicalFields.filter(
        field => !resolutionStrategies.some(strategy => strategy.fieldName === field)
      )
      if (availableConflictFields.length > 0) {
        setSelectedConflictField(availableConflictFields[0])
      }

      // Set first available canonical field as default for validation
      const availableValidationFields = canonicalFields.filter(
        field => !validationRules.some(rule => rule.fieldName === field)
      )
      if (availableValidationFields.length > 0) {
        setSelectedValidationField(availableValidationFields[0])
      }
    }
  }, [uploadResult, canonicalFields.length])

  // Expand data source section when navigating back to dataSource tab
  useEffect(() => {
    if (activeTab === 'dataSource') {
      setIsCollapsed(false)
    }
  }, [activeTab])

  // Fetch S3 CSV files when ingestion method changes to S3 CSV
  useEffect(() => {
    if (formData.defaultIngestionMethod === IngestionMethod.S3_CSV) {
      if (s3CsvFiles.length === 0 && !isLoadingS3Files) {
        fetchS3CsvFiles()
      }
    }
  }, [formData.defaultIngestionMethod])

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }))

    // Clear field error when user starts typing
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field as keyof typeof fieldErrors]
        return newErrors
      })
    }
  }

  // Real-time field validation on blur
  const handleFieldBlur = (field: keyof FormData) => {
    const errors: typeof fieldErrors = {}

    switch (field) {
      case 'sourceName':
        if (!formData.sourceName.trim()) {
          errors.sourceName = 'Source name is required'
        }
        break
      case 'sourceType':
        if (!formData.sourceTypeId) {
          errors.sourceType = 'Source type is required'
        }
        break
      case 'description':
        if (!formData.description.trim()) {
          errors.description = 'Description is required'
        }
        break
      case 'primaryDataCategory':
        if (!formData.primaryDataCategory.trim()) {
          errors.primaryDataCategory = 'Primary data category is required'
        }
        break
      case 'defaultIngestionMethod':
        if (!formData.defaultIngestionMethod.trim()) {
          errors.defaultIngestionMethod = 'Default ingestion method is required'
        }
        break
      default:
        break
    }

    // Only show error for this specific field if it has an error
    if (Object.keys(errors).length > 0) {
      setFieldErrors(prev => ({
        ...prev,
        ...errors
      }))
      setShowFieldErrors(true)
    }
  }

  const handleFileUpload = (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      console.log(validationError)
      return
    }

    setFormData(prev => ({
      ...prev,
      uploadedFile: file,
    }))

    // Clear file upload error when user selects a file
    if (fieldErrors.uploadedFile) {
      setFieldErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.uploadedFile
        return newErrors
      })
    }

    setCriticalFieldError(null)
    setMissingCriticalFields([])
    console.log('File uploaded:', file.name)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }

  const handleBackToDataSources = () => {
    onBackToDataSources?.()
  }


  const handleUploadFile = async () => {
    // First check if file is selected
    if (!formData.uploadedFile) {
      setSubmitError('Please select a file first')
      return
    }

    // Then validate file format
    const validationError = validateFile(formData.uploadedFile)
    if (validationError) {
      setSubmitError(validationError)
      return
    }

    // Finally validate all required fields before upload (skip file validation to avoid duplicate messages)
    const isValid = validateFields(true)
    if (!isValid) {
      setSubmitError('Please fill in all required fields before uploading the file')
      setShowFieldErrors(true)
      return
    }

    // If a data source is already selected, update it and upload the file
    if (selectedDataSource) {
      setIsUploading(true)
      setSubmitError(null)
      setUploadProgress(null)

      try {
        // First, update the data source with current form data
        const matchingSourceType = sourceTypes.find(
          st => st.display_name === formData.sourceType
        )

        const matchingCategory = dataCategories.find(
          cat => cat.name === formData.primaryDataCategory
        )

        const updateData = {
          source_name: formData.sourceName,
          source_type_id: matchingSourceType?.id || (selectedDataSource.sourceId || 1),
          data_category_id: Number(matchingCategory?.id || selectedDataSource.data_category_id || 1),
          ingestion_method: getApiIngestionMethod(formData.defaultIngestionMethod),
          context: formData.description,
          connection_status: selectedDataSource.connection_status || 'connected',
          priority: selectedDataSource.priority || 1,
        }

        const dataSourceId = selectedDataSource.apiId ||
                            selectedDataSource.sourceId ||
                            parseInt(selectedDataSource.id, 10)

        // Update the data source
        const dataSourceIdString = String(selectedDataSource.apiId || selectedDataSource.sourceId || selectedDataSource.id)
        console.log('Updating data source:', dataSourceIdString, updateData)

        await DataSourcesService.updateDataSource(dataSourceIdString, updateData)
        console.log('Data source updated successfully')

        // Then upload the file
        setUploadProgress({
          upload_id: 0,
          filename: formData.uploadedFile.name,
          status: 'UPLOADING',
          progress: 0,
        })

        console.log('Using existing data source:', selectedDataSource.name, 'ID:', dataSourceId)

        const uploadResult = await uploadFileToS3(
          formData.uploadedFile,
          dataSourceId,
          setUploadProgress
        )

        console.log('File uploaded successfully:', uploadResult)

        setIsUploaded(true)

        // Store upload result for later use in analysis
        setUploadResult({
          upload_id: uploadResult?.id || 0,
          data_source_id: dataSourceId,
        })

        // Clear file upload error when upload is successful
        if (fieldErrors.uploadedFile) {
          setFieldErrors(prev => {
            const newErrors = { ...prev }
            delete newErrors.uploadedFile
            return newErrors
          })
        }

        // Reset upload progress
        setUploadProgress(null)

        // Show success message
        setSubmitError('✓ Data source updated and file uploaded successfully!')
        setTimeout(() => setSubmitError(null), 3000)

      } catch (error: any) {
        const errorMessage = extractErrorMessage(error, 'Update and upload failed')
        setSubmitError(errorMessage)
      } finally {
        setIsUploading(false)
      }
      return
    }

    // Validate required form fields only when creating a new data source
    if (!formData.sourceName.trim()) {
      setSubmitError('Source name is required')
      return
    }
    if (!formData.sourceTypeId) {
      setSubmitError('Source type is required')
      return
    }
    if (!formData.primaryDataCategory) {
      setSubmitError('Primary data category is required')
      return
    }

    setIsUploading(true)
    setSubmitError(null)
    setUploadProgress(null)

    try {
      // Create new data source
      console.log('Creating new custom source:', formData)

      // 1. Find category ID for selected dropdown label or id
      const selectedCategory = dataCategories.find(cat =>
        typeof cat === 'string'
          ? cat === formData.primaryDataCategory
          : cat.name === formData.primaryDataCategory ||
            cat.id === formData.primaryDataCategory
      )
      const data_category_id =
        typeof selectedCategory === 'string' ? undefined : selectedCategory?.id

      // 2. Send arrival_schedule to backend
      console.log('Sending arrival_schedule:', formData.arrivalSchedule)

      // 3. Send all entered fields to backend (never hardcode IDs)
      const sourceData = {
        source_name: formData.sourceName,
        source_type_id: formData.sourceTypeId,
        data_category_id,
        ingestion_method: getApiIngestionMethod(formData.defaultIngestionMethod),
        update_frequency: UpdateFrequency.DAILY,
        context: formData.description,
        connection_status: ConnectionStatus.CONNECTED,
        priority: 1,
        arrival_schedule: formData.arrivalSchedule,
      }

      const response = await DataSourcesService.createDataSource(
        sourceData as any
      )

      if (!response.success || !response.data) {
        throw new Error('Failed to create data source')
      }

      const dataSourceId = response.data.id

      // Upload file to S3 with the data source ID
      let uploadResult = null

      if (formData.uploadedFile) {
        try {
          setUploadProgress({
            upload_id: 0,
            filename: formData.uploadedFile.name,
            status: 'UPLOADING',
            progress: 0,
          })

          uploadResult = await uploadFileToS3(
            formData.uploadedFile,
            dataSourceId,
            setUploadProgress
          )

          console.log('File uploaded successfully:', uploadResult)
        } catch (uploadError: any) {
          const uploadErrorMessage = extractErrorMessage(uploadError, 'Unknown error')
          setSubmitError(`Data source created but file upload failed:\n${uploadErrorMessage}`)
          return
        }
      }

      console.log(`Data source "${formData.sourceName}" created successfully and file uploaded to S3!`)

      setIsUploaded(true)

      // Store upload result for later use in analysis
      setUploadResult({
        upload_id: uploadResult?.id || 0,
        data_source_id: dataSourceId,
      })

      // Clear file upload error when upload is successful
      if (fieldErrors.uploadedFile) {
        setFieldErrors(prev => {
          const newErrors = { ...prev }
          delete newErrors.uploadedFile
          return newErrors
        })
      }

      // Reset upload progress
      setUploadProgress(null)
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Upload failed')
      setSubmitError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  const handleUseOriginalChange = (fieldId: number, useOriginal: boolean) => {
    setFieldMappings(prev =>
      prev.map(field =>
        field.id === fieldId ? { ...field, useOriginal } : field
      )
    )
    // Clear save success message when user makes changes
    setSaveSuccess(false)
    setSaveError(null)
  }

  const handleSaveFieldMappings = async () => {
    if (!uploadResult) {
      setSaveError('Upload result not available')
      return
    }

    if (fieldMappings.length === 0) {
      setSaveError('No field mappings to save')
      return
    }

    setIsSaving(true)
    setSaveError(null)
    setSaveSuccess(false)

    try {
      console.log('Saving field mappings with upload_id:', uploadResult.upload_id)
      const saveRequest = fileAnalysisService.convertToSaveRequest(
        fieldMappings,
        uploadResult.upload_id,
        uploadResult.data_source_id
      )

      console.log('Saving field mappings:', saveRequest)

      const response = await fileAnalysisService.saveFieldMappings(saveRequest)

      if (response.success) {
        setSaveSuccess(true)
        setCanonicalFields(response.data.canonical_fields_saved)

        // Set the first canonical field as default for validation field selection
        if (response.data.canonical_fields_saved.length > 0) {
          setSelectedValidationField(response.data.canonical_fields_saved[0])
        }

        console.log(
          `Successfully saved ${response.data.mappings_saved} field mappings`
        )
        console.log(
          'Canonical fields saved:',
          response.data.canonical_fields_saved
        )
      } else {
        throw new Error(response.message || 'Failed to save field mappings')
      }
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error, 'Failed to save field mappings')
      setSaveError(errorMessage)
    } finally {
      setIsSaving(false)
    }
  }

  const handleFinalSubmit = async () => {
    // Existing sources with an upload already linked can skip re-selecting the S3 file
    const hasLinkedUpload = Boolean(
      uploadResult?.upload_id || selectedDataSource?.first_upload_id
    )
    const isValid = validateFields(hasLinkedUpload)
    if (!isValid) {
      setShowFieldErrors(true)
      return
    }

    // Clear any existing field errors
    setShowFieldErrors(false)
    setFieldErrors({})

    // In edit mode, load saved config or run schema analysis if file exists but mappings do not
    if (selectedDataSource) {
      const dataSourceId =
        selectedDataSource.apiId ||
        selectedDataSource.sourceId ||
        parseInt(selectedDataSource.id, 10)
      const uploadId =
        uploadResult?.upload_id ?? selectedDataSource.first_upload_id ?? null

      console.log('Edit mode: opening pipeline for data source:', dataSourceId)

      let hasSavedMappings = fieldMappings.length > 0
      if (!hasSavedMappings && (uploadResult || uploadId)) {
        try {
          const fieldMappingsResponse =
            await fileAnalysisService.getFieldMappings(dataSourceId)

          if (
            fieldMappingsResponse.success &&
            fieldMappingsResponse.data.mappings_count > 0
          ) {
            const mappings = fieldMappingsResponse.data.field_mappings.map(
              (mapping, index) => ({
                id: index + 1,
                fieldName: mapping.canonical_field,
                originalName: mapping.original_field,
                dataType:
                  mapping.data_type ||
                  getFieldDataType(mapping.canonical_field),
                useOriginal: false,
                mapping: mapping.original_field,
                isPreferred: (mapping.confidence_score || 0) >= 0.9,
                confidenceScore: mapping.confidence_score,
              })
            )
            setFieldMappings(mappings)
            setCanonicalFields(mappings.map(m => m.fieldName))
            hasSavedMappings = true
          }
        } catch (error: any) {
          console.error('Error loading field mappings:', error)
        }
      }

      if (!hasSavedMappings && uploadId) {
        if (!uploadResult) {
          setUploadResult({
            upload_id: uploadId,
            data_source_id: dataSourceId,
          })
          setIsUploaded(true)
        }

        const outcome = await runSchemaMappingAnalysis(
          uploadId,
          dataSourceId,
          formData.primaryDataCategory ||
            selectedDataSource.category_name ||
            undefined
        )
        // Stay on Data Source only when the CSV itself must be fixed
        if (outcome === 'critical' || outcome === 'no_category') {
          return
        }
        // success / empty / error → still move forward so the mapping step is reachable
      }

      goToCanonicalFieldsTab()
      return
    }

    // New data source flow: Require file upload
    if (!isUploaded) {
      setSubmitError('Please upload file first')
      return
    }

    if (!uploadResult) {
      setSubmitError('Upload result not available')
      return
    }

    const outcome = await runSchemaMappingAnalysis(
      uploadResult.upload_id,
      uploadResult.data_source_id
    )
    if (outcome === 'critical' || outcome === 'no_category') {
      return
    }

    goToCanonicalFieldsTab()
  }

  return (
    <div className="add-custom-data-source">
      {/* Read-Only Mode Banner */}
      {isReadOnlyMode && (
        <div style={{
          backgroundColor: '#FFF3CD',
          border: '1px solid #FFEAA7',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#856404" strokeWidth="2">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <span style={{
            color: '#856404',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            Read-Only Mode: You can view data but cannot make any changes.
          </span>
        </div>
      )}

      {/* Navigation Bar with Back Arrow and Tabs */}
      <div className="navigation-wrapper">
        <button onClick={handleBackToDataSources} className="breadcrumb-back">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 12H5" />
            <path d="M12 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Tab Navigation Bar */}
        <div className="workflow-tabs-container">
          <div className="workflow-tabs">
            <button
              className={`workflow-tab ${activeTab === 'dataSource' ? 'active' : ''}`}
              onClick={() => setActiveTab('dataSource')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <span>Data Source</span>
            </button>

            <button
              className={`workflow-tab ${activeTab === 'canonicalFields' ? 'active' : ''}`}
              onClick={() => setActiveTab('canonicalFields')}
              disabled={(!showFieldDefinitions && (!uploadResult || fieldMappings.length === 0)) || isAnalyzing}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
              <span>Canonical Field Definitions</span>
            </button>

            <button
              className={`workflow-tab ${activeTab === 'validation' ? 'active' : ''}`}
              onClick={() => setActiveTab('validation')}
              disabled={((!showFieldDefinitions || canonicalFields.length === 0) && (!uploadResult || fieldMappings.length === 0)) || isAnalyzing}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
              <span>Validation & Normalization Rules Agent</span>
            </button>

            <button
              className={`workflow-tab ${activeTab === 'conflictResolution' ? 'active' : ''}`}
              onClick={() => setActiveTab('conflictResolution')}
              disabled={((!showFieldDefinitions || canonicalFields.length === 0) && (!uploadResult || fieldMappings.length === 0)) || isAnalyzing}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>Conflict Resolution Strategies Agent</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="add-source-container">
        {/* Collapsed View */}
        {isCollapsed && activeTab === 'dataSource' && (
          <div className="collapsed-data-source">
            <div className="collapsed-header">
              <div className="collapsed-info">
                <span className="collapsed-name">{formData.sourceName}</span>
                <span className="collapsed-status">✓ Created Successfully</span>
              </div>
              <button
                className="expand-btn"
                onClick={() => setIsCollapsed(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        {!isCollapsed && activeTab === 'dataSource' && (
          <div className="add-source-form">
            {/* Header */}
            <div className="add-source-header">
              <h1 className="add-source-title">Data Source</h1>
              <p className="add-source-subtitle">
                Configure a new custom data source for your financial data
                pipeline
              </p>
            </div>
            <div className="form-row">
              {/* Source Name Field */}
              <div className="form-field">
                <label className="form-label">
                  Source Name <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Custodian PQR"
                  value={formData.sourceName}
                  onChange={isReadOnlyMode ? undefined : (e =>
                    handleInputChange('sourceName', e.target.value)
                  )}
                  onBlur={() => !isReadOnlyMode && handleFieldBlur('sourceName')}
                  disabled={!!selectedDataSource || isReadOnlyMode}
                  readOnly={isReadOnlyMode}
                  style={{
                    opacity: isReadOnlyMode ? 0.7 : 1,
                    cursor: isReadOnlyMode ? 'not-allowed' : 'text',
                    borderColor: fieldErrors.sourceName ? '#dc2626' : undefined,
                  }}
                />
                {showFieldErrors && fieldErrors.sourceName && (
                  <div className="field-error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {fieldErrors.sourceName}
                  </div>
                )}
              </div>

              {/* Source Type Dropdown */}
              <div className="form-field">
                <label className="form-label">
                  Source Type <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
                </label>
                <div className="form-field-select">
                  <select
                    className="form-select"
                    value={formData.sourceTypeId || ''}
                    onChange={isReadOnlyMode ? undefined : (e => {
                      const selectedId = parseInt(e.target.value)
                      const selectedType = sourceTypes.find(t => t.id === selectedId)
                      if (selectedType) {
                        setFormData(prev => ({
                          ...prev,
                          sourceType: selectedType.display_name,
                          sourceTypeId: selectedType.id,
                        }))

                        // Clear sourceType error when user selects a value
                        if (fieldErrors.sourceType) {
                          setFieldErrors(prev => {
                            const newErrors = { ...prev }
                            delete newErrors.sourceType
                            return newErrors
                          })
                        }
                      }
                    })}
                    onBlur={() => !isReadOnlyMode && handleFieldBlur('sourceType' as keyof FormData)}
                    disabled={!!selectedDataSource || isReadOnlyMode}
                    style={{
                      opacity: isReadOnlyMode ? 0.7 : 1,
                      cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                      borderColor: fieldErrors.sourceType ? '#dc2626' : undefined,
                    }}
                  >
                    {sourceTypes.length === 0 && (
                      <option value="">Loading source types...</option>
                    )}
                    {sourceTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.display_name}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="select-arrow"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {showFieldErrors && fieldErrors.sourceType && (
                  <div className="field-error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {fieldErrors.sourceType}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              {/* Expected Arrival Schedule Field */}
              <div className="form-field">
                <label className="form-label" style={{ marginBottom: '12px', display: 'block' }}>Expected Arrival Schedule</label>
                
                {/* Horizontal Day Selection with Single Time */}
                <div className="arrival-schedule-container">
                  {/* Days Row */}
                  <div className="arrival-schedule-days-section">
                    <div className="arrival-schedule-days-row">
                      <span className="arrival-schedule-days-label">Days:</span>
                      {([
                        { day: 'monday', label: 'M' },
                        { day: 'tuesday', label: 'T' },
                        { day: 'wednesday', label: 'W' },
                        { day: 'thursday', label: 'T' },
                        { day: 'friday', label: 'F' },
                        { day: 'saturday', label: 'S' },
                        { day: 'sunday', label: 'S' }
                      ] as const).map(({ day, label }) => (
                        <label 
                          key={day}
                          className={`arrival-schedule-day-item ${!!selectedDataSource ? 'disabled' : ''}`}
                        >
                          <span className="arrival-schedule-day-letter">[{label}]</span>
                          <input
                            type="checkbox"
                            className={`arrival-schedule-day-checkbox ${!!selectedDataSource ? 'disabled' : ''}`}
                            checked={formData.arrivalSchedule.weekly_schedule?.[day]?.enabled || false}
                            onChange={(e) => {
                              const isEnabled = e.target.checked
                              // Get the current time value from any enabled day, or use default
                              const currentTime = Object.values(formData.arrivalSchedule.weekly_schedule || {})
                                .find(d => d?.time)?.time || '09:00:00'
                              
                              setFormData(prev => ({
                                ...prev,
                                arrivalSchedule: {
                                  ...prev.arrivalSchedule,
                                  weekly_schedule: {
                                    ...prev.arrivalSchedule.weekly_schedule!,
                                    [day]: {
                                      enabled: isEnabled,
                                      time: isEnabled ? currentTime : undefined
                                    }
                                  }
                                }
                              }))
                            }}
                            disabled={!!selectedDataSource}
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Time and Timezone Row */}
                  <div className="arrival-schedule-time-row">
                    {/* Time Input */}
                    <div className="arrival-schedule-input-group">
                      <label className="arrival-schedule-input-label">Time:</label>
                      <input
                        type="time"
                        className={`arrival-schedule-time-input ${!!selectedDataSource ? 'disabled' : ''}`}
                        value={(() => {
                          // Get time from any enabled day
                          const enabledDay = Object.values(formData.arrivalSchedule.weekly_schedule || {})
                            .find(d => d?.enabled && d?.time)
                          return enabledDay?.time?.slice(0, 5) || '09:00'
                        })()}
                        onChange={(e) => {
                          const timeValue = e.target.value + ':00'
                          // Update time for all enabled days
                          setFormData(prev => {
                            const updatedSchedule = { ...prev.arrivalSchedule.weekly_schedule! }
                            const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
                            days.forEach((day) => {
                              if (updatedSchedule[day]?.enabled) {
                                updatedSchedule[day] = {
                                  enabled: true,
                                  time: timeValue
                                }
                              }
                            })
                            return {
                              ...prev,
                              arrivalSchedule: {
                                ...prev.arrivalSchedule,
                                weekly_schedule: updatedSchedule
                              }
                            }
                          })
                        }}
                        disabled={!!selectedDataSource}
                      />
                    </div>

                    {/* Timezone Dropdown */}
                    <div className="arrival-schedule-input-group">
                      <label className="arrival-schedule-input-label">Timezone:</label>
                      <select
                        className={`arrival-schedule-timezone-select ${!!selectedDataSource ? 'disabled' : ''}`}
                        value={formData.arrivalSchedule.timezone}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          arrivalSchedule: {
                            ...prev.arrivalSchedule,
                            timezone: e.target.value
                          }
                        }))}
                        disabled={!!selectedDataSource}
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">America/New_York</option>
                        <option value="America/Chicago">America/Chicago</option>
                        <option value="America/Los_Angeles">America/Los_Angeles</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="Asia/Tokyo">Asia/Tokyo</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description Field */}
              <div className="form-field">
                <label className="form-label">
                  Description <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
                </label>
                <textarea
                  className="form-textarea"
                  placeholder="Describe this data source and what type of financial data it provides..."
                  rows={4}
                  value={formData.description}
                  onChange={isReadOnlyMode ? undefined : (e => handleInputChange('description', e.target.value))}
                  onBlur={() => !isReadOnlyMode && handleFieldBlur('description')}
                  disabled={isReadOnlyMode}
                  readOnly={isReadOnlyMode}
                  style={{
                    opacity: isReadOnlyMode ? 0.7 : 1,
                    cursor: isReadOnlyMode ? 'not-allowed' : 'text',
                    height: '180px',
                    resize: 'vertical',
                    borderColor: fieldErrors.description ? '#dc2626' : undefined,
                  }}
                />
                {showFieldErrors && fieldErrors.description && (
                  <div className="field-error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {fieldErrors.description}
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              {/* Primary Data Category */}
              <div className="form-field">
                <label className="form-label">
                  Primary Data Category <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
                </label>
                <div className="form-field-select">
                  <select
                    className="form-select"
                    value={formData.primaryDataCategory}
                    onChange={isReadOnlyMode ? undefined : (e =>
                      handleInputChange('primaryDataCategory', e.target.value)
                    )}
                    onBlur={() => !isReadOnlyMode && handleFieldBlur('primaryDataCategory')}
                    disabled={!!selectedDataSource || isReadOnlyMode}
                    style={{
                      opacity: isReadOnlyMode ? 0.7 : 1,
                      cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                      borderColor: fieldErrors.primaryDataCategory ? '#dc2626' : undefined,
                    }}
                  >
                    {/* Include current value if it's not in the categories list */}
                    {formData.primaryDataCategory &&
                     !dataCategories.some(cat => cat.name === formData.primaryDataCategory) && (
                      <option key={formData.primaryDataCategory} value={formData.primaryDataCategory}>
                        {getDisplayDataCategory(formData.primaryDataCategory)}
                      </option>
                    )}
                    {dataCategories
                      .filter(category => {
                        // Show categories that have display mappings or are explicitly allowed
                        const categoryName = category.name
                        return DATA_CATEGORY_DISPLAY_MAP.hasOwnProperty(categoryName) ||
                               (!categoryName.toLowerCase().includes('transaction') &&
                                !categoryName.toLowerCase().includes('risk') &&
                                !categoryName.toLowerCase().includes('compliance'))
                      })
                      .map(category => (
                        <option key={category.id} value={category.name}>
                          {getDisplayDataCategory(category.name)}
                        </option>
                      ))}
                  </select>
                  <svg
                    className="select-arrow"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {showFieldErrors && fieldErrors.primaryDataCategory && (
                  <div className="field-error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {fieldErrors.primaryDataCategory}
                  </div>
                )}
                {formData.primaryDataCategory && !selectedDataSource && (
                  <div className="category-badge-display">
                    <span className="category-badge-modal">
                      {getDisplayDataCategory(formData.primaryDataCategory)}
                    </span>
                  </div>
                )}
              </div>

              {/* Default Ingestion Method */}
              <div className="form-field">
                <label className="form-label">
                  Default Ingestion Method <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
                </label>
                <div className="form-field-select">
                  <select
                    className="form-select"
                    value={formData.defaultIngestionMethod}
                    onChange={isReadOnlyMode ? undefined : (e =>
                      handleInputChange(
                        'defaultIngestionMethod',
                        e.target.value
                      )
                    )}
                    onBlur={() => !isReadOnlyMode && handleFieldBlur('defaultIngestionMethod')}
                    disabled={!!selectedDataSource || isReadOnlyMode}
                    style={{
                      opacity: isReadOnlyMode ? 0.7 : 1,
                      cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                      borderColor: fieldErrors.defaultIngestionMethod ? '#dc2626' : undefined,
                    }}
                  >
                    {/* Include current value if it's not in the methods list */}
                    {formData.defaultIngestionMethod &&
                     !ingestionMethods.includes(formData.defaultIngestionMethod as IngestionMethod) && (
                      <option key={formData.defaultIngestionMethod} value={formData.defaultIngestionMethod}>
                        {formData.defaultIngestionMethod}
                      </option>
                    )}
                    {ingestionMethods.map(method => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                  <svg
                    className="select-arrow"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                {showFieldErrors && fieldErrors.defaultIngestionMethod && (
                  <div className="field-error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    {fieldErrors.defaultIngestionMethod}
                  </div>
                )}
                {/* Spacer to match category badge height */}
                <div className="category-badge-spacer"></div>
              </div>
            </div>

            {/* Upload Data File - Conditional rendering based on ingestion method */}
            <div className="form-field full-width">
              <label className="form-label">
                {formData.defaultIngestionMethod === IngestionMethod.S3_CSV ? 'Select S3 CSV File' : 'Upload Data File'} <span style={{ color: '#dc2626', fontWeight: 'bold' }}>*</span>
              </label>

              {formData.defaultIngestionMethod === IngestionMethod.S3_CSV ? (
                /* S3 CSV File Selection */
                <>
                  {isLoadingS3Files ? (
                    <div className="s3-files-loading">
                      <div className="spinner"></div>
                      <span>Loading S3 files...</span>
                    </div>
                  ) : s3FilesError ? (
                    <div className="s3-files-error">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      <span>{s3FilesError}</span>
                      <button
                        className="retry-btn"
                        onClick={fetchS3CsvFiles}
                        disabled={isReadOnlyMode}
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="s3-files-dropdown-enhanced">
                        <select
                          className="form-select"
                          value={formData.selectedS3File?.id || ''}
                          onChange={(e) => {
                            if (!isReadOnlyMode && e.target.value) {
                              const selectedFile = s3CsvFiles.find(file => file.id === e.target.value)
                              if (selectedFile) {
                                handleS3FileChange(selectedFile)
                              }
                            }
                          }}
                          disabled={isReadOnlyMode || isSelectingS3File || isUploaded}
                        >
                          <option value="">Select a CSV file from S3...</option>
                          {s3CsvFiles.map(file => (
                            <option key={file.id} value={file.id}>
                              {file.filename} ({(file.file_size / 1024).toFixed(1)} KB)
                            </option>
                          ))}
                        </select>
                        <svg
                          className="select-arrow"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>

                      {/* Live Preview Card */}
                      {formData.selectedS3File && (
                        <div className="s3-preview-card">
                          <div className="s3-preview-header">
                            <svg className="s3-preview-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/>
                              <polyline points="13 2 13 9 20 9"/>
                            </svg>
                            <div className="s3-preview-title-section">
                              <h4 className="s3-preview-filename">{formData.selectedS3File.filename}</h4>
                              <p className="s3-preview-subtitle">Selected CSV File</p>
                            </div>
                            {isUploaded && (
                              <span className="s3-preview-badge s3-preview-badge-success">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20 6 9 17 4 12"/>
                                </svg>
                                Created
                              </span>
                            )}
                            <div className="s3-header-actions">
                              <button
                                className="s3-preview-btn"
                                onClick={() => handleCsvPreview(formData.selectedS3File!)}
                                title="Preview CSV contents"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                  <circle cx="12" cy="12" r="3"/>
                                </svg>
                                Preview
                              </button>
                              <a
                                className="s3-download-btn"
                                href={formData.selectedS3File.s3_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Download CSV file"
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                  <polyline points="7 10 12 15 17 10"/>
                                  <line x1="12" y1="15" x2="12" y2="3"/>
                                </svg>
                                Download
                              </a>
                            </div>
                          </div>

                          <div className="s3-preview-details">
                            <div className="s3-preview-detail-item" style={{ flex: 1 }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                              </svg>
                              <span className="s3-preview-detail-label">Size:</span>
                              <span className="s3-preview-detail-value">{(formData.selectedS3File.file_size / 1024).toFixed(2)} KB</span>
                            </div>
                            <div className="s3-preview-detail-item" style={{ flex: 1 }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                <line x1="16" y1="2" x2="16" y2="6"/>
                                <line x1="8" y1="2" x2="8" y2="6"/>
                                <line x1="3" y1="10" x2="21" y2="10"/>
                              </svg>
                              <span className="s3-preview-detail-label">Uploaded:</span>
                              <span className="s3-preview-detail-value">{new Date(formData.selectedS3File.upload_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                            </div>
                            {/* {formData.selectedS3File.s3_url && (
                              <div className="s3-preview-detail-item">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                                </svg>
                                <span className="s3-preview-detail-label">S3 Location:</span>
                                <a
                                  className="s3-preview-detail-link"
                                  href={formData.selectedS3File.s3_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download File
                                </a>
                              </div>
                            )} */}
                          </div>

                          {!isUploaded && (
                            <button
                              className="s3-use-file-btn"
                              onClick={() => handleS3FileSelect(formData.selectedS3File!)}
                              disabled={isSelectingS3File || isReadOnlyMode}
                            >
                              {isSelectingS3File ? (
                                <>
                                  <div className="spinner"></div>
                                  Creating Data Source...
                                </>
                              ) : (
                                <>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                  Create Data Source with this File
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                /* Manual File Upload */
                <>
                  <input
                    type="file"
                    id="file-upload"
                    className="file-input"
                    accept=".csv,.xlsx,.json"
                    disabled={isReadOnlyMode}
                    onChange={isReadOnlyMode ? undefined : (e => {
                      if (e.target.files && e.target.files[0]) {
                        handleFileUpload(e.target.files[0])
                      }
                    })}
                  />
                  <label
                    htmlFor="file-upload"
                    className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${isReadOnlyMode ? 'disabled' : ''}`}
                    onDragEnter={isReadOnlyMode ? undefined : handleDrag}
                    onDragLeave={isReadOnlyMode ? undefined : handleDrag}
                    onDragOver={isReadOnlyMode ? undefined : handleDrag}
                    onDrop={isReadOnlyMode ? undefined : handleDrop}
                    style={{
                      opacity: isReadOnlyMode ? 0.6 : 1,
                      cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <svg
                      className="upload-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <div className="upload-text">
                      <p className="upload-primary">
                        Upload data file for this source (CSV/Excel/JSON)
                      </p>
                      <p className="upload-secondary">
                        Click to browse or drag and drop. File upload will create
                        the data source automatically.
                      </p>
                    </div>
                  </label>

                  {formData.uploadedFile && (
                    <div className="uploaded-file-info">
                      <div className="file-info-content">
                        <span className="file-name">
                          📄 {formData.uploadedFile.name}
                        </span>
                        <button
                          className={`upload-btn ${isUploading ? 'uploading' : ''} ${isUploaded ? 'uploaded' : ''}`}
                          onClick={isReadOnlyMode ? undefined : handleUploadFile}
                          disabled={
                            isReadOnlyMode ||
                            isUploading ||
                            isUploaded ||
                            !formData.uploadedFile
                          }
                          style={{
                            opacity: isReadOnlyMode ? 0.6 : 1,
                            cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isUploading ? (
                            <>
                              <div className="spinner"></div>
                              Uploading...
                            </>
                          ) : isUploaded ? (
                            <>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22,4 12,14.01 9,11.01" />
                              </svg>
                              Uploaded
                            </>
                          ) : (
                            <>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="17 8 12 3 7 8" />
                                <line x1="12" y1="3" x2="12" y2="15" />
                              </svg>
                              Upload
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* File Upload/Selection Error Message */}
              {showFieldErrors && fieldErrors.uploadedFile && (
                <div className="field-error-message">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {fieldErrors.uploadedFile}
                </div>
              )}
            </div>

            {/* Error/Success Display */}
            {submitError && (
              <div className={typeof submitError === 'string' && submitError.startsWith('✓') ? 'success-message' : 'error-message'}>
                {typeof submitError === 'string' && submitError.startsWith('✓') ? (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ minWidth: '16px', flexShrink: 0 }}
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ minWidth: '16px', flexShrink: 0 }}
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                )}
                <span style={{ whiteSpace: 'pre-line' }}>{String(submitError)}</span>
              </div>
            )}

            {/* Progress Display */}
            {uploadProgress && isUploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress.progress}%` }}
                  ></div>
                </div>
                <span className="progress-text">
                  {uploadProgress.status} - {uploadProgress.progress}%
                </span>
              </div>
            )}

            {/* Error Display for Analysis */}
            {analysisError && (
              <div className="error-message">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                {analysisError}
              </div>
            )}

            {/* Critical Field Validation Error */}
            {criticalFieldError && (
              <div className="error-message">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4" />
                  <circle cx="12" cy="16" r="0.5" fill="currentColor" />
                </svg>
                <div>
                  <div>{criticalFieldError}</div>
                  {missingCriticalFields.length > 0 && (
                    <div style={{ marginTop: '8px', fontSize: '13px' }}>
                      <strong>Action Required:</strong> Add the missing fields to your CSV and re-upload the file.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Processing Error/Success Messages - Only in Edit Flow */}
            {selectedDataSource && isUploaded && uploadResult && isFormValid && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', marginBottom: '16px' }}>
                {/* Processing Error Display */}
                {processingError && (
                  <div className="error-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                    <div>
                      <div>{processingError}</div>
                      {criticalFieldError && (
                        <div className="error-details">
                          <strong>Details:</strong> {criticalFieldError}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Processing Success Display */}
                {processingSuccess && (
                  <div className="success-message">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Processing started successfully!
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons - Side by Side */}
            <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', alignItems: 'center' }}>
              {/* Start Processing Button - Only in Edit Flow with all required fields */}
              {selectedDataSource && isUploaded && uploadResult && isFormValid && (
                <Popup
                  content="Please add at least one Conflict Resolution Strategy before starting processing"
                  disabled={resolutionStrategies.length > 0 || isProcessing || processingSuccess}
                  trigger={
                    <div style={{ display: 'inline-block' }}>
                      <button
                        className={`start-processing-btn ${isProcessing ? 'processing' : ''} ${processingSuccess ? 'success' : ''}`}
                        onClick={handleStartProcessing}
                        disabled={isProcessing || processingSuccess || !uploadResult || resolutionStrategies.length === 0}
                        style={{
                          padding: '12px 24px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: isProcessing || processingSuccess || resolutionStrategies.length === 0 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {isProcessing ? (
                          <>
                            <div className="spinner"></div>
                            Starting processing...
                          </>
                        ) : processingSuccess ? (
                          <>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                            Processing Started
                          </>
                        ) : (
                          <>
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                              <polyline points="3 3 3 8 8 8" />
                            </svg>
                            Start processing
                          </>
                        )}
                      </button>
                    </div>
                  }
                />
              )}

              {/* Next Button - Always visible */}
              <button
                className={`add-data-source-btn ${isAnalyzing ? 'analyzing' : ''}`}
                disabled={!isFormValid || isAnalyzing || isReadOnlyMode}
                onClick={isReadOnlyMode || isAnalyzing ? undefined : handleFinalSubmit}
                style={{
                  opacity:
                    isReadOnlyMode || isAnalyzing || !isFormValid || criticalFieldError
                      ? 0.6
                      : 1,
                  cursor:
                    isReadOnlyMode || isAnalyzing || !isFormValid || criticalFieldError
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {isAnalyzing ? (
                  <>
                    <div className="spinner"></div>
                    Analyzing file...
                  </>
                ) : (
                  <>
                    Next: Configure Mapping
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Field Definitions & Processing Order - CSV */}
        {showFieldDefinitions && activeTab === 'canonicalFields' && (
          <div className="field-mapping-section">
            {/* Section Header with Data Source Selector */}
            <div className="section-header-bar">
              <div className="section-title-group">
                <svg
                  className="section-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#111827"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <ellipse cx="12" cy="5" rx="9" ry="3" />
                  <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                  <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                </svg>
                <h2 className="section-title">
                  Canonical Field Definitions
                </h2>
                <Popup
                  trigger={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  }
                  content="The Schema Mapping agent parsed the uploaded CSV and generated a list of canonical field names. You can use these or override them with the CSV header names. The data will then be mapped to the unified data store after processing."
                  position="top center"
                  style={{ maxWidth: '500px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                />
              </div>
              <div className="section-actions">
                <button
                  className="expand-collapse-btn"
                  onClick={() => toggleSectionExpand('fieldDefinitions')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    {expandedSections.fieldDefinitions ? (
                      <polyline points="18 15 12 9 6 15" />
                    ) : (
                      <polyline points="6 9 12 15 18 9" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Save Status Messages */}
            {expandedSections.fieldDefinitions &&
              (saveError || saveSuccess) && (
                <div className="save-status-messages">
                  {saveError && (
                    <div className="error-message">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      {saveError}
                    </div>
                  )}
                  {saveSuccess && (
                    <div className="success-message">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                      Field mappings saved successfully!
                    </div>
                  )}
                </div>
              )}

            {/* Field Mapping Table - Collapsible */}
            {expandedSections.fieldDefinitions && (
              <div className="field-mapping-table">
                {fieldMappings.length > 0 ? (
                  <>
                    {/* Table Header */}
                    <div className="table-header-row">
                      <div className="header-cell">Field Name</div>
                      {!selectedDataSource && <div className="header-cell">Original Name</div>}
                      <div className="header-cell">Data Type</div>
                      {!selectedDataSource && (
                        <div className="header-cell">
                          <Popup
                            trigger={<span>Use Original</span>}
                            content="Override the agent generated canonical field name with the field name from the original file"
                            position="top center"
                            style={{ maxWidth: '300px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                          />
                        </div>
                      )}
                      {!selectedDataSource && <div className="header-cell">Mapping</div>}
                    </div>

                    {/* Table Rows - Paginated */}
                    {paginatedFields.map(field => (
                      <div key={field.id} className="field-row">
                        <div className="field-cell">
                          <div className="field-name-group">
                            <span className="field-name">
                              {field.fieldName}
                            </span>
                            {field.isPreferred && (
                              <Popup
                                trigger={
                                  <span className="ai-preferred-badge">
                                    AI Preferred{' '}
                                    {field.confidenceScore &&
                                      `(${Math.round(field.confidenceScore * 100)}%)`}
                                  </span>
                                }
                                content="Schema mapping agent's confidence in generating the canonical field name from the original file's field name"
                                position="top center"
                                style={{ maxWidth: '300px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                              />
                            )}
                          </div>
                        </div>
                        {!selectedDataSource && (
                          <div className="field-cell">
                            <span className="original-name">
                              {field.originalName}
                            </span>
                          </div>
                        )}
                        <div className="field-cell">
                          <span className="data-type">{field.dataType}</span>
                        </div>
                        {!selectedDataSource && (
                          <div className="field-cell">
                            <label className="use-original-checkbox">
                              <input
                                type="checkbox"
                                checked={field.useOriginal}
                                onChange={isReadOnlyMode ? undefined : (e =>
                                  handleUseOriginalChange(
                                    field.id,
                                    e.target.checked
                                  )
                                )}
                                disabled={isReadOnlyMode}
                                style={{
                                  cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                                }}
                              />
                              <Popup
                                trigger={<span>Use Original</span>}
                                content="Override the agent generated canonical field name with the field name from the original file"
                                position="top center"
                                style={{ maxWidth: '300px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                              />
                            </label>
                          </div>
                        )}
                        {!selectedDataSource && (
                          <div className="field-cell">
                            <span className="mapping-arrow">{field.mapping}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="no-data-message">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#9CA3AF"
                      strokeWidth="1"
                    >
                      <ellipse cx="12" cy="5" rx="9" ry="3" />
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                    </svg>
                    <p>
                      {isAnalyzing
                        ? 'Running Schema Mapping agent on the uploaded file...'
                        : analysisError
                          ? analysisError
                          : 'No field mappings yet. Open the Data Source tab and click Next: Configure Mapping, or re-open this source to run analysis.'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination Controls */}
            {expandedSections.fieldDefinitions &&
              fieldMappings.length > itemsPerPage && (
                <div className="field-pagination-section">
                  <div className="pagination-info">
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                    {Math.min(currentPage * itemsPerPage, fieldMappings.length)}{' '}
                    of {fieldMappings.length} field mappings
                  </div>

                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentPage(prev => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="15 18 9 12 15 6" />
                      </svg>
                      Previous
                    </button>

                    <div className="pagination-pages">
                      {currentPage > 2 && (
                        <>
                          <button
                            className="pagination-page"
                            onClick={() => setCurrentPage(1)}
                          >
                            1
                          </button>
                          {currentPage > 3 && (
                            <span className="pagination-ellipsis">...</span>
                          )}
                        </>
                      )}

                      {currentPage > 1 && (
                        <button
                          className="pagination-page"
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >
                          {currentPage - 1}
                        </button>
                      )}

                      <button className="pagination-page pagination-page-active">
                        {currentPage}
                      </button>

                      {currentPage <
                        Math.ceil(fieldMappings.length / itemsPerPage) && (
                        <button
                          className="pagination-page"
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >
                          {currentPage + 1}
                        </button>
                      )}

                      {currentPage <
                        Math.ceil(fieldMappings.length / itemsPerPage) - 1 && (
                        <>
                          {currentPage <
                            Math.ceil(fieldMappings.length / itemsPerPage) -
                              2 && (
                            <span className="pagination-ellipsis">...</span>
                          )}
                          <button
                            className="pagination-page"
                            onClick={() =>
                              setCurrentPage(
                                Math.ceil(fieldMappings.length / itemsPerPage)
                              )
                            }
                          >
                            {Math.ceil(fieldMappings.length / itemsPerPage)}
                          </button>
                        </>
                      )}
                    </div>

                    <button
                      className="pagination-btn"
                      onClick={() =>
                        setCurrentPage(prev =>
                          Math.min(
                            prev + 1,
                            Math.ceil(fieldMappings.length / itemsPerPage)
                          )
                        )
                      }
                      disabled={
                        currentPage ===
                        Math.ceil(fieldMappings.length / itemsPerPage)
                      }
                    >
                      Next
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

            {/* Save Button at Bottom of Field Mapping Section */}
            {expandedSections.fieldDefinitions && (
              <div className="field-mapping-save-section">
                <button
                  className="back-btn"
                  onClick={() => setActiveTab('dataSource')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
                <button
                  className={`save-btn ${isSaving ? 'saving' : ''}`}
                  onClick={async () => {
                    if (!isReadOnlyMode) {
                      if (!selectedDataSource) {
                        await handleSaveFieldMappings();
                      }
                      setActiveTab('validation');
                    }
                  }}
                  disabled={isSaving || fieldMappings.length === 0 || isReadOnlyMode}
                  style={{
                    opacity: isReadOnlyMode ? 0.6 : 1,
                    cursor: isReadOnlyMode ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSaving ? (
                    <>
                      <div className="spinner"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      {!selectedDataSource && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                          <polyline points="17 21 17 13 7 13 7 21" />
                          <polyline points="7 3 7 8 15 8" />
                        </svg>
                      )}
                      {selectedDataSource ? 'Next' : 'Save & Next'}
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Validation & Normalization Rules Section */}
        {showFieldDefinitions && activeTab === 'validation' && (
          <div className="validation-section">
            <div className="validation-header">
              <div className="validation-title-group">
                <svg
                  className="validation-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                >
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
                <h3 className="validation-title">
                  Validation & Normalization rules
                </h3>
                <Popup
                  trigger={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  }
                  content="Validation and Transformation AI Agent parses the rules defined in plain text and applies to the data"
                  position="top center"
                  style={{ maxWidth: '500px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                />
              </div>
              <div className="section-actions">
                <button
                  className="expand-collapse-btn"
                  onClick={() => toggleSectionExpand('validation')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    {expandedSections.validation ? (
                      <polyline points="18 15 12 9 6 15" />
                    ) : (
                      <polyline points="6 9 12 15 18 9" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {/* Validation Content - Collapsible */}
            {expandedSections.validation && (
              <div className="validation-content">
                {/* Show placeholder message when no canonical fields are saved */}
                {canonicalFields.length === 0 ? (
                  <div className="validation-placeholder">
                    <div className="placeholder-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9CA3AF"
                        strokeWidth="1"
                      >
                        <path d="M9 12l2 2 4-4" />
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    </div>
                    <p className="placeholder-text">
                      Save the field mappings to provide validation rules
                    </p>
                    <p className="placeholder-subtext">
                      Field mappings must be saved first before you can create
                      validation rules.
                    </p>
                  </div>
                ) : (
                  /* Hide interactive elements when data source is selected */
                  !selectedDataSource && (
                    <>
                      <label className="validation-label">Select Field</label>
                      <div className="select-field-wrapper">
                        {canonicalFields.filter(field => !validationRules.some(rule => rule.fieldName === field)).length > 0 ? (
                          <div className="validation-select-with-badge">
                            <span className="field-type-tag">
                              {getFieldDataType(selectedValidationField)}
                            </span>
                            <select
                              className="validation-field-select-with-tag"
                              value={selectedValidationField}
                              onChange={e =>
                                setSelectedValidationField(e.target.value)
                              }
                            >
                              {canonicalFields
                                .filter(field => !validationRules.some(rule => rule.fieldName === field))
                                .map(field => (
                                  <option key={field} value={field}>
                                    {field}
                                  </option>
                                ))}
                            </select>
                          </div>
                        ) : (
                          <div className="no-fields-available">
                            <p>All fields have validation rules defined. Delete a rule to add a different one.</p>
                          </div>
                        )}
                      </div>

                      <label className="validation-label">
                        Describe your validation rule in plain language
                      </label>

                      <div className="validation-input-container">
                        <textarea
                          className="validation-textarea"
                          placeholder="Example for client_identifier: 'Ensure values follow the XXX-XX-XXXX format and remove dashes before storing' or 'Must be unique and not empty'"
                          value={ruleDescription}
                          onChange={e => setRuleDescription(e.target.value)}
                        />
                        <button className="ai-icon-btn" title="AI Assistant">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                          </svg>
                        </button>
                      </div>

                      <p className="validation-helper-text">
                        AI will convert your description into executable
                        validation rules for the selected field
                      </p>

                      {/* Rule Save Error Display */}
                      {ruleSaveError && (
                        <div className="error-message">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                          {ruleSaveError}
                        </div>
                      )}

                      <div className="validation-actions">
                        <button
                          className={`generate-btn ${isSavingRule ? 'saving' : ''}`}
                          onClick={handleGenerateRule}
                          disabled={
                            isSavingRule ||
                            !ruleDescription.trim() ||
                            canonicalFields.filter(field => !validationRules.some(rule => rule.fieldName === field)).length === 0
                          }
                        >
                          {isSavingRule ? (
                            <>
                              <div className="spinner"></div>
                              Saving Rule...
                            </>
                          ) : (
                            <>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                              Generate & Save Rule
                            </>
                          )}
                        </button>
                        <button
                          className="clear-btn"
                          onClick={handleClearRule}
                          disabled={isSavingRule}
                        >
                          Clear
                        </button>
                      </div>
                    </>
                  )
                )}

                {/* Saved Rules List */}
                <div className="saved-rules-section">
                  <h4 className="saved-rules-title">
                    Saved Rules ({validationRules.length})
                  </h4>

                  {validationRules.length > 0 ? (
                    <div className="saved-rules-table-container">
                      <table className="saved-rules-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Validation Rule</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validationRules.map(rule => (
                            <tr key={rule.id}>
                              <td>
                                <span className="field-badge">
                                  {rule.fieldName}
                                </span>
                              </td>
                              <td>
                                <div className="rule-with-icon">
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#10B981"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <path d="M9 12l2 2 4-4" />
                                  </svg>
                                  <span>{rule.description}</span>
                                </div>
                              </td>
                              <td className="created-time">{rule.createdAt}</td>
                              <td>
                                <button
                                  className={`delete-rule-btn ${deletingRuleId === rule.id ? 'deleting' : ''}`}
                                  onClick={() => handleDeleteRule(rule.id)}
                                  title={selectedDataSource ? "Delete disabled in edit mode" : "Delete rule"}
                                  disabled={deletingRuleId === rule.id || !!selectedDataSource}
                                  style={{
                                    opacity: selectedDataSource ? 0.5 : 1,
                                    cursor: selectedDataSource ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {deletingRuleId === rule.id ? (
                                    <div className="spinner"></div>
                                  ) : (
                                    <svg
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      <line x1="10" y1="11" x2="10" y2="17" />
                                      <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-rules-message">
                      <p>No validation rules saved yet.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Validation Section Navigation Buttons */}
            {expandedSections.validation && (
              <div className="field-mapping-save-section">
                <button
                  className="back-btn"
                  onClick={() => setActiveTab('canonicalFields')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
                <button
                  className="next-btn"
                  onClick={() => setActiveTab('conflictResolution')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#00D4AA',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Conflict Resolution Strategies Section */}
        {showFieldDefinitions && activeTab === 'conflictResolution' && (
          <div className="conflict-section">
            <div className="conflict-header">
              <div className="conflict-title-group">
                <svg
                  className="conflict-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#E17100"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="conflict-title">
                  Conflict Resolution Strategies
                </h3>
                <Popup
                  trigger={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#6B7280"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ cursor: 'pointer', marginLeft: '4px' }}
                    >
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="16" x2="12" y2="12"/>
                      <line x1="12" y1="8" x2="12.01" y2="8"/>
                    </svg>
                  }
                  content="Conflicts resolution agent parses the strategies defined under each rule and resolves the conflict between old/new data in our datastore"
                  position="top center"
                  style={{ maxWidth: '500px', textAlign: 'center', backgroundColor: '#FFFFFF', color: '#1F2937' }}
                />
              </div>
              <div className="section-actions">
                <button
                  className="expand-collapse-btn"
                  onClick={() => toggleSectionExpand('conflict')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6b7280',
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    {expandedSections.conflict ? (
                      <polyline points="18 15 12 9 6 15" />
                    ) : (
                      <polyline points="6 9 12 15 18 9" />
                    )}
                  </svg>
                </button>
              </div>
            </div>

            {expandedSections.conflict && (
              <div className="conflict-content">
                {/* Show placeholder message when no canonical fields are saved */}
                {canonicalFields.length === 0 ? (
                  <div className="conflict-placeholder">
                    <div className="placeholder-icon">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#9CA3AF"
                        strokeWidth="1"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <p className="placeholder-text">
                      Save the field mappings to provide conflict resolution
                      strategies
                    </p>
                    <p className="placeholder-subtext">
                      Field mappings must be saved first before you can create
                      resolution strategies.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Hide interactive elements when data source is selected */}
                    {!selectedDataSource && (
                      <>
                        <label className="conflict-label">Select Field</label>
                        <div className="select-field-wrapper">
                          {canonicalFields.filter(field => !resolutionStrategies.some(strategy => strategy.fieldName === field)).length > 0 ? (
                            <div className="conflict-select-with-badge">
                              <span className="field-type-tag">
                                {getFieldDataType(selectedConflictField)}
                              </span>
                              <select
                                className="conflict-field-select-with-tag"
                                value={selectedConflictField}
                                onChange={e =>
                                  setSelectedConflictField(e.target.value)
                                }
                              >
                                {canonicalFields
                                  .filter(field => !resolutionStrategies.some(strategy => strategy.fieldName === field))
                                  .map(field => (
                                    <option key={field} value={field}>
                                      {field}
                                    </option>
                                  ))}
                              </select>
                            </div>
                          ) : (
                            <div className="no-fields-available">
                              <p>All fields have resolution strategies defined. Delete a strategy to add a different one.</p>
                            </div>
                          )}
                        </div>

                        <label className="conflict-label">
                          Resolution Strategy
                        </label>
                        <div className="resolution-select-wrapper">
                        <svg
                          className="clock-icon"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#3B82F6"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        <select
                          className="resolution-strategy-select"
                          value={selectedResolutionStrategy}
                          onChange={e =>
                            setSelectedResolutionStrategy(e.target.value)
                          }
                        >
                          {resolutionStrategyOptions.map(strategy => (
                            <option key={strategy} value={strategy}>
                              {strategy}
                            </option>
                          ))}
                        </select>
                      </div>

                      <p className="ai-suggestion-text">
                        <svg
                          className="ai-star-icon"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="#7C3AED"
                        >
                          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                        </svg>
                        AI will analyze the conflict and select the most
                        accurate value based on data context
                      </p>

                      {/* Strategy Save Error Display */}
                      {strategySaveError && (
                        <div className="error-message">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                          {strategySaveError}
                        </div>
                      )}

                      <div className="conflict-actions">
                        <button
                          className={`add-resolution-btn ${isSavingStrategy ? 'saving' : ''}`}
                          onClick={handleAddResolution}
                          disabled={
                            isSavingStrategy ||
                            !selectedConflictField ||
                            canonicalFields.filter(field => !resolutionStrategies.some(strategy => strategy.fieldName === field)).length === 0
                          }
                        >
                          {isSavingStrategy ? (
                            <>
                              <div className="spinner"></div>
                              Saving Strategy...
                            </>
                          ) : (
                            <>
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                              </svg>
                              Add Resolution Rule
                            </>
                          )}
                        </button>
                        <button
                          className="clear-btn"
                          disabled={isSavingStrategy}
                          onClick={() => setStrategySaveError(null)}
                        >
                          Clear
                        </button>
                      </div>
                    </>
                  )}

                  {/* Saved Resolution Rules Table */}
                  <div className="saved-resolution-section">
                    <h4 className="saved-resolution-title">
                      Saved Resolution Rules ({resolutionStrategies.length})
                    </h4>

                  {resolutionStrategies.length > 0 ? (
                    <div className="saved-resolution-table-container">
                      <table className="saved-resolution-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Resolution Strategy</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resolutionStrategies.map(strategy => (
                            <tr key={strategy.id}>
                              <td>
                                <div className="field-column">
                                  <span className="field-name-text">
                                    {strategy.fieldName}
                                  </span>
                                  <span className="field-type-badge">
                                    {getFieldDataType(strategy.fieldName)}
                                  </span>
                                </div>
                              </td>
                              <td>
                                <div className="table-strategy-wrapper">
                                  <svg
                                    className="table-strategy-icon"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#10B981"
                                    strokeWidth="2"
                                  >
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  <span className="strategy-display">
                                    {strategy.strategy}
                                  </span>
                                </div>
                              </td>
                              <td className="description-text">
                                {strategy.description}
                              </td>
                              <td>
                                <button
                                  className={`delete-rule-btn ${deletingStrategyId === strategy.id ? 'deleting' : ''}`}
                                  onClick={() =>
                                    handleDeleteStrategy(strategy.id)
                                  }
                                  title={selectedDataSource ? "Delete disabled in edit mode" : "Delete strategy"}
                                  disabled={deletingStrategyId === strategy.id || !!selectedDataSource}
                                  style={{
                                    opacity: selectedDataSource ? 0.5 : 1,
                                    cursor: selectedDataSource ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {deletingStrategyId === strategy.id ? (
                                    <div className="spinner"></div>
                                  ) : (
                                    <svg
                                      width="18"
                                      height="18"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                    >
                                      <polyline points="3 6 5 6 21 6" />
                                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                      <line x1="10" y1="11" x2="10" y2="17" />
                                      <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                  )}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-strategies-message">
                      <p>No resolution strategies saved yet.</p>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            )}

            {/* Conflict Resolution Section Navigation and Action Buttons */}
            {expandedSections.conflict && (
              <div className="field-mapping-save-section">
                <button
                  className="back-btn"
                  onClick={() => setActiveTab('validation')}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>

                {!selectedDataSource && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'flex-end' }}>
                    {/* Processing Error Display */}
                    {processingError && (
                      <div className="error-message">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                        <div>
                          <div>{processingError}</div>
                          {criticalFieldError && (
                            <div className="error-details">
                              <strong>Details:</strong> {criticalFieldError}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Processing Success Display */}
                    {processingSuccess && (
                      <div className="success-message">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                          <polyline points="22 4 12 14.01 9 11.01" />
                        </svg>
                        Processing started successfully!
                      </div>
                    )}

                    <Popup
                      content="Please add at least one Conflict Resolution Strategy before starting processing"
                      disabled={resolutionStrategies.length > 0 || isProcessing || processingSuccess}
                      trigger={
                        <div style={{ display: 'inline-block' }}>
                          <button
                            className={`start-processing-btn ${isProcessing ? 'processing' : ''} ${processingSuccess ? 'success' : ''}`}
                            onClick={handleStartProcessing}
                            disabled={isProcessing || processingSuccess || !uploadResult || resolutionStrategies.length === 0}
                          >
                            {isProcessing ? (
                              <>
                                <div className="spinner"></div>
                                Starting processing...
                              </>
                            ) : processingSuccess ? (
                              <>
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                  <polyline points="22 4 12 14.01 9 11.01" />
                                </svg>
                                Processing Started
                              </>
                            ) : (
                              <>
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                                  <polyline points="3 3 3 8 8 8" />
                                </svg>
                                Start processing
                              </>
                            )}
                          </button>
                        </div>
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* CSV Preview Modal */}
      {showCsvPreview && (
        <div className="csv-preview-overlay" onClick={() => setShowCsvPreview(false)}>
          <div className="csv-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="csv-preview-modal-header">
              <div className="csv-preview-modal-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/>
                  <polyline points="13 2 13 9 20 9"/>
                </svg>
                <span>{formData.selectedS3File?.filename}</span>
                {csvPreviewRows.length > 0 && (
                  <span className="csv-preview-row-count">{csvPreviewRows.length} rows (preview)</span>
                )}
              </div>
              <button className="csv-preview-close-btn" onClick={() => setShowCsvPreview(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="csv-preview-modal-body">
              {csvPreviewLoading && (
                <div className="csv-preview-loading">
                  <div className="csv-preview-spinner"></div>
                  <span>Loading preview...</span>
                </div>
              )}

              {csvPreviewError && (
                <div className="csv-preview-error">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <p>{csvPreviewError}</p>
                  {formData.selectedS3File?.s3_url && (
                    <a href={formData.selectedS3File.s3_url} target="_blank" rel="noopener noreferrer" className="csv-preview-fallback-link">
                      Download file instead ↗
                    </a>
                  )}
                </div>
              )}

              {!csvPreviewLoading && !csvPreviewError && csvPreviewHeaders.length > 0 && (
                <div className="csv-preview-table-wrap">
                  <table className="csv-preview-table">
                    <thead>
                      <tr>
                        <th className="csv-preview-row-num">#</th>
                        {csvPreviewHeaders.map((h, i) => (
                          <th key={i}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreviewRows.map((row, ri) => (
                        <tr key={ri}>
                          <td className="csv-preview-row-num">{ri + 1}</td>
                          {csvPreviewHeaders.map((_, ci) => (
                            <td key={ci}>{row[ci] ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AddCustomDataSource
